/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import ReactDOM from 'react-dom'
import Hls from 'hls.js'
import PlayerControls from '../PlayerControls'
import styles from './Player.module.scss'
import { PlayerProps, PlayerState } from './types'
import classnames from 'classnames'
import get from 'lodash/get'
import { SeekEvent } from '../PlayerControls/SeekBar/types'
import { addBreadcrumb, Severity, captureMessage } from '@sentry/browser'
import { debug } from '@bt-react/libs/Logger'

export interface PlayerInterface {
  onManifestParsed(): void
  initialiseHls(): Hls | null
  autoPlayVideo(video: HTMLVideoElement): void
  play(startFrom?: number): Promise<boolean>
  pause(pauseAt?: number): number
  seekHandler(event: SeekEvent): void
  controlsSubscriber(stream): void
  togglePlay(): void
  onEnd(manuallyEnded?: boolean): void
  duration: number
  currentTime: number
  volume: number
  isPlaying: boolean
  paused: boolean
}

class Player extends React.Component<PlayerProps, PlayerState> implements PlayerInterface {

  private hls: Hls | null = null
  private playerRef: HTMLVideoElement | null = null
  private callbackRef = node => {

    if (!node) return
    this.playerRef = node
    this.setState({ setRef: true })

  }
  private controlsStream$
  private playinSubscriber

  state = {
    currentTime: 0,
    duration: 0,
    setRef: false
  }

  constructor(props) {
    super(props)
    /* @ts-ignore */
    this.containerEl = document.createElement("div");
  }

  componentDidMount () {

    this.playinSubscriber = this.props.subscriber
    this.setListeners()

     /* @ts-ignore */
     this.iframe.contentDocument.body.appendChild(this.containerEl);
     const styles = document.createElement('style')
     styles.append(`
 
     body {
       margin: 0;
     }
 
     div {
       align-items: center;
       justify-content: center;
       display: flex;
       height: 100%;
     }
 
     .Player--fullscreen {
      display: block;
      object-fit: contain;
      width: 100%;
      height: 100%;
    }`)
     /* @ts-ignore */
     this.iframe.contentDocument.head.appendChild(styles)

  }

  componentDidUpdate (prevProps: PlayerProps, prevState: PlayerState) {

    if (!prevState.setRef && this.state.setRef && this.props.source && this.shouldInitialiseHls) this.hls = this.initialiseHls()

    if (prevProps.source !== this.props.source) {

      if (this.shouldInitialiseHls) {

        this.hls = this.initialiseHls()

      }
      else {

        if (this.hls) {

          // @ts-ignore
          this.hls.off(Hls.Events.LEVEL_SWITCHED)

        }

      }

    }

  }

  componentWillUnmount () {

    if (this.playerRef) {

      this.removeListeners()

    }

    if (this.hls) {

      // @ts-ignore
      this.hls.off(Hls.Events.MANIFEST_PARSED)
      // @ts-ignore
      this.hls.off(Hls.Events.LEVEL_SWITCHED)
      // @ts-ignore
      this.hls.off(Hls.Events.ERROR)

    }

  }

  get shouldInitialiseHls (): boolean {

    return !!this.playerRef && this.props.source &&
      this.props.source.indexOf('m3u8') !== -1

  }

  onCanPlay = () => {

    /** If onReadyToPlay is passed and this isn't an HLS implementation,
     * we need to tell the parent that the video is ready to play. If this is
     * an HLS implementation this is triggered through onManifestParsed
    */
    if(this.props.onReadyToPlay && !this.hls) this.props.onReadyToPlay()

  }

  setListeners = () => {

    if (!this.playerRef) return
    this.playerRef.addEventListener('timeupdate', this.setCurrentTime)
    this.playerRef.addEventListener('durationchange', this.onDurationChange)
    this.playerRef.addEventListener('canplay', this.onCanPlay)

  }

  removeListeners = () => {

    if (!this.playerRef) return
    this.playerRef.removeEventListener('timeupdate', this.setCurrentTime)
    this.playerRef.removeEventListener('durationchange', this.onDurationChange)
    this.playerRef.removeEventListener('canplay', this.onCanPlay)

  }

  setCurrentTime = ({ target }) => {

    this.setState({ currentTime: target.currentTime })

  }

  onDurationChange = ({ target }) => {

    if (this.props.source && this.props.source.indexOf('m3u8') > -1) return
    this.durationUpdateMP4(target.duration)

  }

  durationUpdateMP4 = (duration: number) => this.setState({ duration })

  onManifestParsed = () => {

    if (!this.playerRef) return

    this.props.onReadyToPlay && this.props.onReadyToPlay()
    this.autoPlayVideo(this.playerRef)

  }

  initialiseHls = () => {

    if (!this.playerRef) return this.hls

    if (this.hls) this.hls.destroy()

    const hls = new Hls({ enableWorker: false })
    hls.on(Hls.Events.MANIFEST_PARSED, this.onManifestParsed)
    hls.on(Hls.Events.LEVEL_SWITCHED, (event, { level }) => {

      if (!hls.levels[level]) return
      const duration = get(hls.levels[level], 'details.totalduration', 0)
      if (typeof duration === 'number') this.setState({ duration })
      if (this.props.onDurationChange) this.props.onDurationChange(duration)

    })

    hls.on(Hls.Events.ERROR, function (event, data) {

      if (data.fatal) {

        switch (data.type) {

          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad()
            break
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError()
            break

        }

      }

    })

    hls.loadSource(this.props.source)
    hls.attachMedia(this.playerRef)
    return hls

  }

  autoPlayVideo (video: HTMLVideoElement): void {

    if (this.props.disableAutoplay) return

    const promise = video.play()

    if (typeof promise !== 'undefined') {

      promise.catch(e => {

        addBreadcrumb({
          category: 'playback',
          message: get(e, 'message', 'Failed to start track in <Player />'),
          data: {
            trackName: get(this.props, 'source', 'Unavailable')
          },
          level: Severity.Info
        })
        captureMessage('Failed to start track in Player', Severity.Debug)
        debug(e)

      })

    }

  }

  get paused (): boolean {

    return get(this.playerRef, 'paused', false)

  }

  get duration (): number {

    if (!this.playerRef) return 0
    return this.playerRef.duration

  }

  get currentTime (): number {

    if (!this.playerRef) return 0
    return this.playerRef.currentTime

  }

  set currentTime (time: number) {

    if (!this.playerRef || time > this.playerRef.duration) return
    this.playerRef.currentTime = time

  }

  /** Returns a volume from 0 to 1 representing a percentage of max volume */
  get volume (): number {

    if (!this.playerRef) return 0
    return this.playerRef.volume

  }

  /** Set the volume, clamped between 0 and 1 */
  set volume (volume: number) {

    if (!this.playerRef) return
    this.playerRef.volume = Math.max(Math.min(1, volume), 0)

  }

  get isPlaying (): boolean {

    if (!this.playerRef) return false

    return !!(
      this.playerRef.currentTime > 0 &&
      !this.playerRef.paused &&
      !this.playerRef.ended &&
      this.playerRef.readyState > 2
    )

  }

  async play (startFrom?: number): Promise<boolean> {

    if (!this.playerRef) return false
    if (startFrom) this.currentTime = startFrom

    if (this.props.onPlay) this.props.onPlay(startFrom)

    try {

      await this.playerRef.play()
      return true

    }
    catch (e) {

      return false

    }

  }

  pause (pauseAt?: number): number {

    if (!this.playerRef) return 0
    this.playerRef.pause()
    if (pauseAt) this.currentTime = pauseAt
    return this.playerRef.currentTime

  }

  seekHandler = (event: SeekEvent) => {

    if (!this.playerRef) return

    this.playerRef.currentTime = event.position * this.state.duration

    if (event.type === 'seek') {

      if (this.props.onSeek) this.props.onSeek(this.playerRef.currentTime)

    }

    if (event.type === 'seekComplete') {

      if (this.props.onSeekComplete) this.props.onSeekComplete(this.playerRef.currentTime)

    }

  }

  controlsSubscriber = (stream): void => {

    this.controlsStream$ = stream

    this.controlsStream$.subscribe({ next: this.seekHandler.bind(this) })

  }

  onError = error => {
    if (this.isPlaying) this.pause()

    this.props.onError?.( error )
  }
  togglePlay = () => {

    if (this.props.onRemoteClick) return this.toggleRemotePlay()

    if (this.isPlaying) this.pause()
    else {

      this.play()
        .catch(e => {

          debug('play error ', e)

        })

    }

  }

  toggleRemotePlay = () => {

    if (!this.props.onRemoteClick) return
    if (this.isPlaying) this.props.onRemoteClick('pause', get(this.playerRef, 'currentTime'))
    else this.props.onRemoteClick('play', get(this.playerRef, 'currentTime'))

  }

  onEnd = (manuallyEnded = false) => {

    if (this.props.onEnd) this.props.onEnd(manuallyEnded)

  }

  render () {

    const PlayerContainerClasses = classnames(
      styles['Player-container'], {
        [styles['Player-container--controls']]: this.props.controlsVisible
      }
    )

    return <div className={PlayerContainerClasses} >
      <div className={styles[`Player-screen`]} >

        <iframe
          title={`frame-player`} scrolling='no' frameBorder='0' style={{width: '100%', height: '100%'}}
          /* @ts-ignore */
          ref={el => (this.iframe = el)}
        >
        {/* @ts-ignore */}
        {
          ReactDOM.createPortal(
            <video
            muted={this.props.muteAudio}
            autoPlay={!this.props.disableAutoplay}
            className='Player--fullscreen'
            ref={this.callbackRef}
            data-bdd={this.props.dataBdd ? this.props.dataBdd : 'player'}
            src={this.props.source}
            onClick={this.togglePlay}
            onEnded={() => this.onEnd()}
            onPause={() => this.props.disablePause && this.togglePlay()}
            preload='auto'
            onError={this.onError}
          />,
          /* @ts-ignore */
          this.containerEl
          )
        }
        </iframe>
      </div>
      {
        this.playerRef && this.props.controlsVisible &&
        <PlayerControls
          onPlay={this.togglePlay}
          onStop={() => this.onEnd(true)}
          duration={this.state.duration}
          currentTime={this.currentTime}
          parentRef={this.playerRef}
          subscriber={this.controlsSubscriber}
          playing={this.isPlaying}
        />
      }
    </div>

  }

}

export default Player
